import React from "react";
import { Mail } from "lucide-react";
import { PageHero, Section, Container } from "../components/PageUI";
import SEO from "../components/SEO";

const sections = [
  {
    heading: "1. Information We Collect",
    intro: "Depending on how you use Masjid Suite, we may collect:",
    items: [
      "Account information: such as your name, email address, phone number, login details, and account preferences.",
      "Masjid administration information: such as Masjid name, address, contact details, administrator information, verification information, prayer times, programs, announcements, events, and uploaded content.",
      "Donation and transaction information: such as donation amount, selected Masjid or campaign, transaction status, payment-related references, and refund or chargeback information. Full payment card details may be collected and processed by the applicable payment provider rather than stored directly by Harmonia Enterprises LLC.",
      "Advertising information: such as advertiser details, submitted advertisement content, billing information, approval records, communications, and campaign details.",
      "Usage and technical information: such as IP address, device type, operating system, browser type, app activity, pages or features used, interaction logs, error records, and approximate location derived from technical data where applicable.",
      "Communications: such as messages, support requests, complaints, feedback, and other correspondence you send to us.",
    ],
  },
  {
    heading: "2. How We Collect Information",
    intro: "We may collect information:",
    items: [
      "Directly from you when you register, contact us, submit content, create a Masjid, submit an advertisement, or make a donation;",
      "From Masjid administrators or authorized representatives who provide information through the platform;",
      "Automatically when you access or use our website, application, dashboard, or kiosk-related services;",
      "From payment processors, hosting providers, analytics providers, notification providers, or other service providers involved in operating Masjid Suite.",
    ],
  },
  {
    heading: "3. How We Use Your Information",
    intro: "We may use personal information to:",
    items: [
      "Create, operate, and secure user accounts;",
      "Provide Masjid Suite features and services;",
      "Display prayer times, Masjid profiles, programs, events, announcements, campaigns, and advertisements;",
      "Facilitate donations and transaction-related communications;",
      "Verify Masjid administrators, advertisers, campaigns, or account information;",
      "Send requested or enabled notifications and service communications;",
      "Review, approve, reject, or investigate advertisements and submitted content;",
      "Provide customer support and respond to requests;",
      "Improve usability, reliability, performance, and security;",
      "Detect and prevent fraud, misuse, security incidents, and unlawful activity;",
      "Comply with legal obligations and enforce our agreements.",
    ],
    outro: "We do not sell your personal information to third parties.",
  },
  {
    heading: "4. Donations and Payment Processing",
    body: "If you make a donation or payment through Masjid Suite, transaction processing may be provided by a third-party payment provider. That provider may collect payment information needed to process your transaction under its own terms and privacy policy. Harmonia Enterprises LLC may receive information about the transaction, such as the amount, recipient or campaign, transaction reference, status, date, payout information, refund status, or chargeback information, as needed to operate the relevant feature, provide support, prevent fraud, or comply with legal obligations.",
  },
  {
    heading: "5. How We Share Information",
    intro: "We may share information in the following circumstances:",
    items: [
      "With Masjids: where needed to process or document a donation, support an event or program registration, or provide features you choose to use.",
      "With service providers: such as payment processors, hosting providers, analytics providers, notification providers, support providers, and technology vendors that help operate Masjid Suite.",
      "For legal or safety reasons: where required by law, regulation, legal process, or where reasonably necessary to protect rights, safety, security, prevent fraud, or enforce our agreements.",
      "In a business transaction: if Harmonia Enterprises LLC is involved in a merger, acquisition, financing, restructuring, or sale of assets, subject to applicable law.",
      "With your direction or consent: where you request or authorize sharing.",
    ],
  },
  {
    heading: "6. Public and Community Content",
    body: "Certain information submitted through Masjid Suite may be intended for public or community display, such as Masjid names, prayer times, addresses, contact details, announcements, events, programs, donation campaigns, images, and approved advertisements. Masjid administrators and advertisers are responsible for ensuring that they have permission to publish content and personal information submitted for public or community display.",
  },
  {
    heading: "7. Cookies, Analytics and Similar Technologies",
    body: "Our website or application may use cookies, device identifiers, logs, analytics tools, or similar technologies to operate the service, remember preferences, improve performance, understand feature usage, and protect the platform from fraud or misuse. Where required by applicable law, we will provide relevant choices or obtain required consent for non-essential cookies or similar technologies.",
  },
  {
    heading: "8. Data Security",
    body: "We use reasonable administrative, technical, and organizational measures designed to protect personal information from unauthorized access, misuse, loss, alteration, or disclosure. However, no method of online transmission or electronic storage is completely secure. We cannot guarantee absolute security of information transmitted to or stored through Masjid Suite.",
  },
  {
    heading: "9. Data Retention",
    body: "We retain personal information only for as long as reasonably necessary to provide Masjid Suite, maintain transaction or administrative records, comply with legal obligations, resolve disputes, prevent fraud, enforce our agreements, and protect the platform and its users. Retention periods may differ depending on the type of information, the services used, contractual requirements, payment provider requirements, and applicable law.",
  },
  {
    heading: "10. Your Privacy Rights",
    body: "Depending on the privacy laws that apply to you, you may have the right to request access to, correction of, deletion of, restriction of processing of, or a copy of certain personal information. You may also have rights to object to certain processing or withdraw consent where processing is based on your consent. To submit a privacy request, contact us using the email address below. We may need to verify your identity before completing a request. Some requests may be subject to legal exceptions, retention duties, or other limitations under applicable law.",
  },
  {
    heading: "11. Children's Privacy",
    body: "Masjid Suite is not intended to knowingly collect personal information from children where parental or guardian consent is required under applicable law. If you believe a child has provided personal information without required consent, please contact us so that we can review the matter and take appropriate action.",
  },
  {
    heading: "12. International Use and Data Transfers",
    body: "Masjid Suite may be accessed by users in different countries. Information may be processed or stored in countries other than the country in which you are located, including where our service providers operate. Where required by applicable law, Harmonia Enterprises LLC will take appropriate steps relating to international transfers of personal information.",
  },
  {
    heading: "13. Third-Party Links and Services",
    body: "Masjid Suite may contain links to third-party websites or use third-party services. This Privacy Policy does not govern the privacy practices of third parties. You should review the privacy policies of third-party services before providing information directly to them.",
  },
  {
    heading: "14. Changes to This Privacy Policy",
    body: "Harmonia Enterprises LLC may update this Privacy Policy from time to time as Masjid Suite evolves or as required by applicable law. Updated versions will be posted through Masjid Suite or otherwise made available with an updated effective date.",
  },
];

export default function PrivacyPolicy() {
  return (
    <div>
      <SEO
        title="Privacy Policy | Masjid Suite"
        description="Learn how Harmonia Enterprises LLC collects, uses, stores, shares, and protects personal information when you use Masjid Suite."
        path="/privacy-policy"
      />
      <PageHero
        eyebrow="Legal"
        title="Privacy Policy"
        lead={<>Effective Date: July 18, 2026</>}
      />

      <Section className="bg-section-alt overflow-hidden">
        <Container>
          <div className="max-w-3xl mx-auto">
            <p className="text-[#4b5563] text-[15px] leading-relaxed">
              This Privacy Policy describes how Harmonia Enterprises LLC (&ldquo;Company&rdquo;, &ldquo;we&rdquo;, &ldquo;us&rdquo;, or &ldquo;our&rdquo;) collects, uses, stores, shares, and protects personal information when you use Masjid Suite. Masjid Suite is a product of Harmonia Enterprises LLC. References to &ldquo;Masjid Suite&rdquo; in this Privacy Policy refer to the platform, mobile application, website, kiosk features, donation tools, prayer time features, advertising features, and related services operated or provided by Harmonia Enterprises LLC.
            </p>

            <div className="mt-12 space-y-12">
              {sections.map((s, i) => (
                <div key={i}>
                  <h2 className="font-display text-2xl text-[#1d1d1f] font-semibold mb-4">{s.heading}</h2>
                  {s.intro && <p className="text-[#4b5563] text-[15px] leading-relaxed mb-3">{s.intro}</p>}
                  {s.items && (
                    <ul className="space-y-2.5 text-[15px] text-[#4b5563] leading-relaxed">
                      {s.items.map((item, j) => (
                        <li key={j} className="flex items-start gap-2">
                          <span className="w-1.5 h-1.5 rounded-full bg-[#c9a34a] mt-2.5 shrink-0" />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                  {s.outro && <p className="text-[#4b5563] text-[15px] leading-relaxed mt-4">{s.outro}</p>}
                  {s.body && <p className="text-[#4b5563] text-[15px] leading-relaxed">{s.body}</p>}
                </div>
              ))}

              <div>
                <h2 className="font-display text-2xl text-[#1d1d1f] font-semibold mb-4">15. Contact Us</h2>
                <p className="text-[#4b5563] text-[15px] leading-relaxed mb-3">
                  For questions, concerns, or privacy-related requests concerning Masjid Suite, please contact Harmonia Enterprises LLC at:
                </p>
                <a href="mailto:support@harmoniaenterprisesllc.com" className="inline-flex items-center gap-2 text-[#0e2408] font-medium">
                  <Mail size={15} /> support@harmoniaenterprisesllc.com
                </a>
              </div>
            </div>
          </div>
        </Container>
      </Section>
    </div>
  );
}
